import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';

import type {
  BillingCatalogDto,
  CompanyBillingSummaryDto,
  BillingSubscriptionDetailsDto,
} from '@shared/services/apiService';

import { billingDate, formatBillingMoney, formatCreditUnits } from '../model/formatBillingUnits';
import { billingStatusLabels } from './BillingSummary';

export const BillingPlanOverview = ({
  summary,
  catalog,
  subscription,
  inlinePlans,
  busy,
  onViewPlans,
}: {
  summary: CompanyBillingSummaryDto;
  catalog?: BillingCatalogDto;
  subscription?: BillingSubscriptionDetailsDto | null;
  inlinePlans: boolean;
  busy: boolean;
  onViewPlans: () => void;
}) => {
  const paid = summary.status === 'paid';
  const current = summary.plan;
  const offer = catalog?.plans.find((plan) => plan.id === current?.id);
  const details = subscription?.planVersionId === current?.id ? subscription : null;
  const price = details ?? offer;
  const endsAt = summary.endsAt ?? summary.previousPeriod?.endsAt;
  return (
    <Paper
      component="section"
      aria-label="Current company plan"
      variant="outlined"
      sx={{ p: { xs: 2, md: 2 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption">{paid ? 'Current plan' : 'Current access'}</Typography>
          <Stack direction="row" alignItems="center" spacing={1.5} useFlexGap flexWrap="wrap">
            <Typography
              variant="h6"
              component="h2"
              fontWeight={600}
              sx={{ overflowWrap: 'anywhere' }}>
              {paid && current ? current.key : billingStatusLabels[summary.status]}
            </Typography>
            {paid && current && catalog && !offer && <Chip size="small" label="Legacy plan" />}
            {paid && price && (
              <Typography variant="body2" color="text.secondary">
                {formatBillingMoney(price.amountMinor, price.currency, price.currencyScale)} /{' '}
                {details?.intervalCount && details.intervalCount > 1
                  ? `${details.intervalCount} `
                  : ''}
                {details?.interval ?? 'month'}
              </Typography>
            )}
          </Stack>
          {current && (
            <Typography variant="body2" color="text.secondary">
              {formatCreditUnits(current.allowanceUnits, current.creditScale)} credits per period ·{' '}
              {current.concurrentLimit} simultaneous calls
            </Typography>
          )}
          {endsAt && (
            <Typography variant="body2" color="text.secondary">
              {summary.status === 'expired'
                ? 'Period ended'
                : paid && details?.status === 'active' && !details.cancelAtPeriodEnd
                  ? 'Renews'
                  : 'Period ends'}
              : {billingDate(endsAt)}
            </Typography>
          )}
          {paid && details && !details.cancelAtPeriodEnd && (
            <Typography variant="caption" component="p" color="text.secondary">
              Next base plan payment:{' '}
              {formatBillingMoney(details.amountMinor, details.currency, details.currencyScale)} ·
              excludes future purchases and tax adjustments
            </Typography>
          )}
          {paid && details?.startsAt && details.endsAt && (
            <Typography variant="caption" color="text.secondary">
              Service period: {billingDate(details.startsAt)} – {billingDate(details.endsAt)}
            </Typography>
          )}
        </Box>
        <Stack direction="row" alignItems="center" spacing={2} useFlexGap flexWrap="wrap">
          <Chip
            label={billingStatusLabels[summary.status]}
            color={summary.status === 'review' ? 'warning' : 'default'}
            sx={(theme) => {
              const state =
                theme.palette.status[summary.status === 'review' ? 'warning' : 'neutral'];
              return {
                color: state.foreground,
                bgcolor: state.background,
                border: '1px solid',
                borderColor: state.border,
              };
            }}
          />
          {!inlinePlans && !!catalog?.plans.length && (
            <Button
              variant="contained"
              disabled={busy}
              aria-haspopup="dialog"
              onClick={onViewPlans}>
              {summary.canManage && catalog.canChangePlan ? 'Change plan' : 'View plans'}
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};
